import cx from 'classnames';
import {RefObject, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {MarketingBanner} from '~/common/components/MegaNav/MegaNavVariant/components/LocalNav/components/Dropdown/components/MarketingBanner/MarketingBanner';
import type {
  CategoryPositionType,
  InvitationCategoryType,
  SubCategoryType,
} from '~/common/components/MegaNav/MegaNavVariant/types';
import {SubCategoryAssets} from './components/SubCategory/components/SubCategoryAssets/SubCategoryAssets';
import {SubCategory} from './components/SubCategory/SubCategory';
import * as styles from './Dropdown.module.scss';

type SubCategoriesProps = {
  alignDropdownToLeft: boolean;
  categoryPosition: CategoryPositionType | null;
  hoveredCategory: InvitationCategoryType | null;
  isMegaNavOpen: boolean;
  localNavRef: RefObject<HTMLDivElement | null>;
  onClickOutside: () => void;
};

export const Dropdown = ({
  hoveredCategory,
  categoryPosition,
  alignDropdownToLeft,
  isMegaNavOpen,
  onClickOutside,
  localNavRef,
}: SubCategoriesProps) => {
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (categoryPosition && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      const container = localNavRef.current;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const dropdownRect = dropdown.getBoundingClientRect();

        let left: number;
        let width: string;

        if (alignDropdownToLeft) {
          // Left-aligned with category button, but width fits content
          left = categoryPosition.left;
          width = 'auto'; // Let the content determine the width

          // Check if dropdown would go outside the right edge of the container
          if (left + dropdownRect.width > containerRect.width) {
            left = Math.max(0, containerRect.width - dropdownRect.width);
          }
        } else {
          // Default centered positioning
          left = 0;
          width = '100%';
        }

        setDropdownStyle({
          left: `${left}px`,
          width: width,
        });
      }
    }
  }, [categoryPosition, localNavRef, alignDropdownToLeft]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMegaNavOpen &&
        dropdownContentRef.current &&
        !dropdownContentRef.current.contains(event.target as Node)
      ) {
        onClickOutside();
      }
    };

    if (isMegaNavOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMegaNavOpen, onClickOutside]);

  if (!hoveredCategory || !isMegaNavOpen) return null;

  const {subCategories, assets} = hoveredCategory;

  if (!subCategories.length) {
    return null;
  }

  return (
    <div className={styles.dropdown} ref={dropdownRef} style={dropdownStyle}>
      <div
        className={cx(styles.dropdownContent, {
          [styles.fitContent]: !alignDropdownToLeft,
        })}
        ref={dropdownContentRef}
      >
        <div className={styles.categoriesContainer}>
          {subCategories?.map((subCategory: SubCategoryType) => (
            <SubCategory key={subCategory.display_name} subCategory={subCategory} />
          ))}
          <SubCategoryAssets assets={assets} />
        </div>
        <MarketingBanner hoveredCategory={hoveredCategory} />
      </div>
    </div>
  );
};
